import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { supabase } from '@/integrations/supabase/client';
import { DEFAULT_AUTHED_HOME } from '@/lib/landingAuthCopy';
import { getPendingJoin, clearPendingJoin } from '@/lib/pendingJoin';

// Handles native custom-scheme returns (com.sabotagefantasy.app://...). Mounted app-wide.
// DORMANT on web and on old native builds that lack @capacitor/app natively: on web the
// isNativePlatform() guard returns early; on an old binary the dynamic import / listener
// registration fails and simply never fires. That is expected for Build 6 Phase A.
//
//   auth-callback?code=&next=      -> exchangeCodeForSession, then navigate to next
//   paypal-return?token=&league=   -> paypal-capture-order, then navigate to /wallet
//   paypal-return?cancel=1         -> back to the deposit screen, no capture
//   persona-return                 -> /persona-return (polls check_kyc_gate)
//
// This routes returns only. All money authority stays server-side in
// paypal-capture-order (caller JWT, pending-row + user_id + league_id checks,
// idempotent complete_deposit_atomic).

// Parse com.sabotagefantasy.app://<host>?<query>. Custom schemes are unreliable in
// URL(), so fall back to a manual split.
function parseSchemeUrl(raw: string): { host: string; params: URLSearchParams } | null {
  try {
    const u = new URL(raw);
    const host = u.host || u.pathname.replace(/^\/+/, '');
    if (host) return { host, params: u.searchParams };
  } catch {
    /* fall through */
  }
  const m = raw.match(/^[^:]+:\/\/([^/?#]+)(?:\?([^#]*))?/);
  if (!m) return null;
  return { host: m[1], params: new URLSearchParams(m[2] || '') };
}

export default function NativeUrlHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    let active = true;
    let remove: (() => void) | undefined;

    const closeBrowser = async () => {
      try {
        const { Browser } = await import('@capacitor/browser');
        await Browser.close().catch(() => {});
      } catch {
        /* browser plugin optional */
      }
    };

    const onUrl = async (rawUrl: string) => {
      const parsed = parseSchemeUrl(rawUrl);
      if (!parsed) return;
      const { host, params } = parsed;

      if (host === 'auth-callback') {
        await closeBrowser();
        const code = params.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (!error && active) {
            const next = params.get('next');
            navigate(next && next.startsWith('/') ? next : DEFAULT_AUTHED_HOME, { replace: true });
          }
        }
        return;
      }

      if (host === 'paypal-return') {
        await closeBrowser();
        // SAB-595: if this deposit was funding a paid-league join, return to the
        // join page instead of the wallet. The join page re-reads the balance, so
        // it shows "Join League" on success or the shortfall again on abandon —
        // graceful either way.
        const pendingJoin = getPendingJoin();
        if (params.get('cancel')) {
          if (active) {
            if (pendingJoin) { clearPendingJoin(); navigate(`/nfl/join/${pendingJoin.code}`, { replace: true }); }
            else navigate('/wallet/deposit', { replace: true });
          }
          return;
        }
        const token = params.get('token');
        const league = params.get('league');
        if (token && league) {
          try {
            await supabase.functions.invoke('paypal-capture-order', {
              body: { orderId: token, leagueId: league },
            });
          } catch {
            /* capture failures surface on the wallet page; row stays pending for reconcile */
          }
        }
        if (active) {
          if (pendingJoin) { clearPendingJoin(); navigate(`/nfl/join/${pendingJoin.code}`, { replace: true }); }
          else navigate('/wallet', { replace: true });
        }
        return;
      }

      if (host === 'persona-return') {
        // Persona hosted redirect deep-linked back from the external browser. Hand
        // off to the in-app /persona-return page, which polls the gate and surfaces
        // the same actionable "still processing" state on a delayed webhook, one
        // return experience for web and native. (pendingKyc in localStorage carries
        // the return context across the round trip.)
        await closeBrowser();
        if (active) navigate('/persona-return', { replace: true });
        return;
      }
    };

    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appUrlOpen', ({ url }) => { onUrl(url); });
        if (!active) { handle.remove(); return; }
        remove = () => handle.remove();
      } catch {
        /* @capacitor/app not registered natively (old build): nothing to listen on */
      }
    })();

    return () => { active = false; remove?.(); };
  }, [navigate]);

  return null;
}
